import cn from 'classnames'
import { base_colorAlert } from 'ext-ui/style/colors'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import SvgFlagNew from 'src/components/Svgs/SvgFlagNew'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  TransactionListModel,
  TransactionManagerRejectionReason,
  transactionManagerReviewRejectionReasonToString,
  TransactionManagerReviewStatus,
} from 'src/models/transaction.models'
import { useQueryAdditionalFields } from 'src/queries/organizations/additionalFields.queries'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import { useQueryUser } from 'src/queries/users.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectOrganizationReceiptMgmtEnabledFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { toDollars } from 'src/utils/currency.utils'

import style from './TaskItem.style'

interface TaskItemProps {
  task: TransactionListModel
}

const TaskItem = ({ task }: TaskItemProps): React.ReactElement => {
  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()
  const { show_expense_requirements: showAdditionalFields } = useSelector(
    selectFeatureFlaggerFromState,
  )

  // check for required receipts that are missing
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const receiptsMissing =
    receiptManagementEnabled &&
    task.attachmentsCount === 0 &&
    task.receiptRequired

  // check for required exp cats that are missing
  const { data: mandatoryExpenseDetails } = useQueryExpenseCategories({
    filters: { active: true, requiredForTransactions: true },
  })
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const expCatsMissing =
    task.isCleared &&
    task.checkMissingRequiredExpenseCategory(
      activeIntegration?.connectedPlatform,
      mandatoryExpenseDetails,
    )

  // Fetch additional fields data
  const { data: mandatoryAdditionalFields } = useQueryAdditionalFields({
    filters: { active: true, required: true },
  })
  const additionalFieldsMissing =
    task.isCleared &&
    !!mandatoryAdditionalFields?.some((field) => {
      const fieldDetails = Object.values(task?.additionalFields || {})?.find(
        (txnFields) => txnFields.expenseAdditionalFieldId === field.id,
      )?.details
      return fieldDetails === undefined
    })

  const taskIsRejectedForSpenderAction =
    managerReviewRulesEnabled &&
    task.managerReviewStatus === TransactionManagerReviewStatus.Rejected &&
    [
      TransactionManagerRejectionReason.MissingReceipt,
      TransactionManagerRejectionReason.IncorrectReceipt,
      TransactionManagerRejectionReason.MissingExpenseCategories,
      TransactionManagerRejectionReason.IncorrectExpenseCategories,
      TransactionManagerRejectionReason.MissingNote,
    ].includes(
      task?.managerRejectionReason as TransactionManagerRejectionReason,
    )

  const { data: manager } = useQueryUser({
    userId: task?.managerReviewId,
    enabled: taskIsRejectedForSpenderAction,
  })

  const title = useMemo(() => {
    if (taskIsRejectedForSpenderAction) {
      return translate('Action Required: ${rejectionReason}', {
        rejectionReason: translate(
          transactionManagerReviewRejectionReasonToString(
            task.managerRejectionReason,
          ),
        ),
      })
    } else {
      if (showAdditionalFields) {
        let missingCopy
        if (receiptsMissing) {
          missingCopy = translate('Missing Receipt')
        }
        if (expCatsMissing) {
          missingCopy = translate('Missing Expense Categories')
        }
        if (additionalFieldsMissing) {
          missingCopy = translate('Missing Required Details')
        }
        // are 2+ missing?
        if (
          [receiptsMissing, expCatsMissing, additionalFieldsMissing].filter(
            (val) => val,
          ).length >= 2
        ) {
          missingCopy = translate('Missing Requirements')
        }
        return missingCopy
      } else {
        return translate('Transaction for ${amount} from ${merchant}', {
          merchant: task.merchantName?.toUpperCase() || '',
          amount: currencyFormatter({
            amount: toDollars(task.clearingBillingAmountCents || 0),
            currency: task.clearingBillingCurrency,
          }),
        })
      }
    }
  }, [
    task,
    translate,
    currencyFormatter,
    receiptsMissing,
    expCatsMissing,
    additionalFieldsMissing,
    taskIsRejectedForSpenderAction,
    showAdditionalFields,
  ])

  const titleIcon = useMemo(() => {
    if (taskIsRejectedForSpenderAction) {
      return (
        <SvgFlagNew
          fill={base_colorAlert}
          className={'rejectionFlagIcon'}
          width={14}
          height={14}
        />
      )
    } else {
      return undefined
    }
  }, [task, taskIsRejectedForSpenderAction])

  const subcopy = useMemo(() => {
    if (taskIsRejectedForSpenderAction) {
      return translate(
        task.managerRejectionNote
          ? '${manager} has rejected this transaction: "${rejectionNote}"'
          : '${manager} has rejected this transaction.',
        {
          manager: manager?.fullNameOrEmail || '',
          rejectionNote: task.managerRejectionNote || '',
        },
      )
    } else {
      if (showAdditionalFields) {
        return translate('Transaction for ${amount} from ${merchant}', {
          merchant: task.merchantName?.toUpperCase() || '',
          amount: currencyFormatter({
            amount: toDollars(task.clearingBillingAmountCents || 0),
            currency: task.clearingBillingCurrency,
          }),
        })
      } else {
        let missingCopy
        if (receiptsMissing) {
          missingCopy = translate('Missing Receipt')
        }
        if (expCatsMissing) {
          missingCopy = translate('Missing Expense Categories')
        }
        // are both missing?
        if (expCatsMissing && receiptsMissing) {
          missingCopy = translate('Missing Expense Categories & Receipt')
        }
        return missingCopy
      }
    }
  }, [
    task,
    translate,
    currencyFormatter,
    manager,
    receiptsMissing,
    expCatsMissing,
    taskIsRejectedForSpenderAction,
    showAdditionalFields,
  ])

  return (
    <div className={cn('TaskItem', task.id)} css={style}>
      <div className={'flex-row align-center gap-1'}>
        {titleIcon}
        <div className={cn('TaskItem-title')}>{title}</div>
      </div>
      <div className={cn('TaskItem-body')}>{subcopy}</div>
    </div>
  )
}

export default TaskItem
